import { getBoundHostOrigin } from '~/utils/hostOrigin';

/**
 * Base type for all plugin host messages.
 * Plugin-specific message types should extend this.
 */
export type PluginHostMessage = {
  type: string;
  data?: unknown;
};

/**
 * Send a message to the parent plugin host using the bound host origin.
 * Falls back to '*' only when no origin has been bound yet (should not happen
 * for post-Init messages since the handshake binds the origin first).
 */
export function sendPluginHostMessage<T extends PluginHostMessage>(message: T): void {
  if (typeof window !== 'undefined' && window.parent !== window) {
    const targetOrigin = getBoundHostOrigin() ?? '*';
    window.parent.postMessage(message, targetOrigin);
  }
}

/**
 * Send a ready notification to the plugin host.
 * Uses '*' because the host origin is not yet known before the Init handshake.
 */
export function sendPluginReadyMessage<T extends PluginHostMessage>(message: T): void {
  if (typeof window !== 'undefined' && window.parent !== window) {
    window.parent.postMessage(message, '*');
  }
}

/**
 * Check if we're running inside a plugin iframe
 */
export function isInPluginIframe(): boolean {
  if (typeof window === 'undefined') return false;
  return window.top !== window.self;
}

/**
 * Validates that a URL uses a safe protocol (http or https).
 * Prevents javascript:, data:, vbscript:, and other dangerous schemes.
 */
export function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Creates a URL opener function for use in plugin iframes.
 * Plugin iframes don't support window.open directly, so we send a message
 * to the parent plugin host to open URLs.
 *
 * @param createMessage - Function that creates the message to send for opening a URL
 * @returns A function that opens URLs via the plugin host
 */
export function createPluginUrlOpener<T extends PluginHostMessage>(
  createMessage: (url: string) => T,
): (url: string) => void {
  return (url: string) => {
    if (!isSafeUrl(url)) {
      return;
    }
    sendPluginHostMessage(createMessage(url));
  };
}
